import React, { useEffect, useRef } from 'react';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import HabitatAssayLozenges from 'src/shared/components/lozenges/HabitatAssayLozenges';
import useProjectBreadcrumb from 'src/shared/hooks/useProjectBreadcrumb';
import { AppName } from 'src/global/globalConfig';
import { getCurrentSampleComparisonUIState } from 'src/app/ecology-hub/state/sample-comparison/sampleComparisonSelector';
import { useAppDispatch, useAppSelector } from 'src/store';
import { updateFilters, SampleComparisonFiltersState } from 'src/app/ecology-hub/state/sample-comparison/sampleComparisonSlice';
import Loader from 'src/shared/components/loader/Loader';
import { useSampleComparisonQuery } from 'src/app/ecology-hub/state/api/ecologyHubGraphSlice';
import { transformData } from 'src/app/ecology-hub/views/sample-comparison/SampleComparisonHelpers';
import HorizontalBarChart, { HorizontalBarChartData } from 'src/shared/components/charts/horizontal-bar-chart/HorizontalBarChart';
import { chartExplainerSlugKey } from 'src/shared/constants/chartExplainerKeys';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import useProjectFilters from 'src/shared/hooks/useProjectFilters';
import DownloadImageLogo from 'src/shared/components/download-image-logo/DownloadImageLogo';
import Show from 'src/shared/components/show/Show';
import useProject from 'src/shared/hooks/useProject';
import DemoProjectBanner from 'src/shared/components/demo-project-banner/DemoProjectBanner';
import NoData from 'src/shared/components/no-data/NoData';
import HexagonBoxPlot from 'src/assets/images/ecology-hub/hexagon-boxplots.png';
import SampleComparisonDownloadImageButton from './components/sample-comparison-download-image-button/SampleComparisonDownloadImageButton';
import SampleComparisonFilters from './components/sample-comparison-filters/SampleComparisonFilters';
import styles from './SampleComparison.module.scss';

const PAGE_TITLE = 'Sample Comparison';
const SampleComparison = () => {
    const chartRef = useRef<HTMLDivElement>(null);
    const { isStarterProject } = useProject();
    const {
        currentProjectId,
        currentProjectFilters,
        currentProjectSelectedHabitatAssayType: habitatAssayType,
        setCurrentProjectHabitatAssayType,
        getColourForArea,
        samplingEventFilterData,
    } = useProjectFilters();

    const currentProjectBreadcrumbDetails = useProjectBreadcrumb('Sample Comparison');
    const dispatch = useAppDispatch();

    const uiState = useAppSelector(getCurrentSampleComparisonUIState);
    const {
        filters: { chartId, sortBy, samplingEvent },
        chartTitle,
    } = uiState;

    const chartTitleEl = (
        <span>
            {chartTitle}
            {chartExplainerSlugKey[chartId] && <QuestionButtonHelp type='api' placement='right' slug={chartExplainerSlugKey[chartId]} />}
        </span>
    );

    const { currentData, isFetching } = useSampleComparisonQuery(
        {
            projectId: currentProjectId || '',
            samples: {
                habitatAssay: [habitatAssayType?.key || ''],
                fromDate: samplingEvent?.fromDate,
                toDate: samplingEvent?.toDate,
            },
        },
        {
            skip: !currentProjectId || !habitatAssayType || !samplingEvent,
        }
    );

    const onFiltersChange = (fragment: Partial<SampleComparisonFiltersState>) => {
        dispatch(updateFilters(fragment));
    };

    useEffect(() => {
        if (!samplingEventFilterData || samplingEventFilterData.length === 0) {
            return;
        }

        dispatch(
            updateFilters({
                samplingEvent: samplingEventFilterData[0],
            })
        );
    }, [samplingEventFilterData]);

    if (isFetching) {
        return (
            <>
                <Loader />
            </>
        );
    }

    if (!currentData) {
        return null;
    }

    const { samples } = currentData.sampleComparisonData;
    const [sortProperty, sortOrder] = sortBy.split('_');

    const transformedData = transformData(samples, chartId, getColourForArea, sortProperty as keyof HorizontalBarChartData, sortOrder);
    const onHabitatAssayTypeChange = (value: string) => {
        onFiltersChange({ chartId: '' });
        setCurrentProjectHabitatAssayType(value);
    };

    const lozenges = (
        <HabitatAssayLozenges
            options={currentProjectFilters?.habitatAssayTypeOptions || []}
            selectedValue={habitatAssayType?.key || ''}
            onChange={onHabitatAssayTypeChange}
            app={AppName.SAMPLE_COMPARISON}
        />
    );

    // subtitle for image download
    const selectedHabitat = currentProjectFilters?.habitatAssayTypeOptions?.find(option => option.value === habitatAssayType?.key);
    const subTitleForDownload = (
        <div className='text-primary text-sm font-bold mt-2 ml-[200px] hidden' id='data-chart-subtitle'>
            {`${selectedHabitat?.habitatName} ${selectedHabitat?.assayName}`}
        </div>
    );

    const mainContent = (
        <Show
            when={transformedData.length > 0}
            fallback={<NoData image={HexagonBoxPlot} text='There is not enough data to display' subText='Please select another Test.' />}
        >
            <div className='relative' ref={chartRef}>
                <div className={styles.chartContainer}>
                    {/* NM logo for image downaload */}
                    <DownloadImageLogo
                        containerClass='absolute right-[9%] top-10 hidden'
                        textClass='absolute top-[-5px] right-0 text-[17px] text-grey-80'
                    />
                    <HorizontalBarChart data={transformedData} title={chartTitleEl} subTitle={subTitleForDownload} yAxisTile='Sample ID' />
                </div>
            </div>
        </Show>
    );

    const filterSection = (
        <div className='flex justify-end items-end gap-5'>
            <SampleComparisonFilters />
            {/* Display Download image button for non-starter subscription */}
            <Show when={!isStarterProject}>
                <div className='w-[1px] h-10 bg-grey-20'>&nbsp;</div>
                <SampleComparisonDownloadImageButton chartContainer={chartRef} />
            </Show>
        </div>
    );

    return (
        <StandardAppLayout
            breadcrumbs={currentProjectBreadcrumbDetails}
            lozenges={lozenges}
            title={PAGE_TITLE}
            subTitle='Compare metric values across samples'
            mainContent={mainContent}
            filterSection={filterSection}
            topBanner={<DemoProjectBanner pageName={AppName.SAMPLE_COMPARISON} />}
        />
    );
};

export default SampleComparison;
